import React, { useState, useEffect } from 'react'
import { Joyride, STATUS } from 'react-joyride'
import { Button, Dialog, Tooltip } from 'components/ui'
import { HiOutlineQuestionMarkCircle } from 'react-icons/hi'
import { useTour } from 'utils/tours/TourProvider'

const JOYRIDE_LOCALE = {
    back: 'Atrás',
    close: 'Cerrar',
    last: 'Finalizar',
    next: 'Siguiente',
    skip: 'Omitir',
}

const JOYRIDE_STYLES = {
    options: {
        primaryColor: '#2563eb',
        zIndex: 10000,
    },
    tooltip: {
        borderRadius: '1rem',
        padding: '1.25rem',
        fontSize: '0.875rem',
    },
    buttonNext: {
        borderRadius: '0.5rem',
        padding: '0.5rem 1rem',
        fontWeight: 'bold',
    },
    buttonBack: {
        marginRight: '0.5rem',
        fontWeight: '500',
    },
}

export function TourHelpButton({ onClick, className = '', title = 'Ver guía de esta pantalla' }) {
    return (
        <Tooltip title={title}>
            <button
                type="button"
                onClick={onClick}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-vitalia-50 hover:text-vitalia-600 dark:hover:bg-vitalia-900/30 dark:hover:text-vitalia-300 transition-colors border border-gray-200 dark:border-gray-600 ${className}`}
            >
                <HiOutlineQuestionMarkCircle className="text-base text-vitalia-600 dark:text-vitalia-400" />
                <span>Guía</span>
            </button>
        </Tooltip>
    )
}

// Pregunta una sola vez (por pantalla, por usuario) si quiere un recorrido
// guiado y, si acepta, corre los `steps` de react-joyride sobre esta vista.
export default function PageTour({
    tourKey,
    steps,
    title = '¿Deseas un recorrido guiado por esta pantalla?',
    description = 'Te mostramos paso a paso las funciones principales y cómo utilizarlas.',
    showHelpButton = true,
    scrollOffset = 250,
}) {
    const { listo, hasSeenTour, markTourSeen } = useTour()
    const [showPrompt, setShowPrompt] = useState(false)
    const [runTour, setRunTour] = useState(false)
    const [tourInstanceKey, setTourInstanceKey] = useState(0)

    useEffect(() => {
        if (listo && !hasSeenTour(tourKey)) {
            setShowPrompt(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listo, tourKey])

    const aceptar = () => {
        setShowPrompt(false)
        setTourInstanceKey((prev) => prev + 1)
        setRunTour(true)
    }

    const rechazar = () => {
        setShowPrompt(false)
        markTourSeen(tourKey)
    }

    const iniciarManual = () => {
        setShowPrompt(false)
        setRunTour(false)
        setTourInstanceKey((prev) => prev + 1)
        setTimeout(() => {
            setRunTour(true)
        }, 50)
    }

    const handleJoyrideCallback = (data) => {
        const { status, action } = data
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED || action === 'close') {
            setRunTour(false)
            markTourSeen(tourKey)
        }
    }

    return (
        <>
            <Dialog isOpen={showPrompt} onClose={rechazar} onRequestClose={rechazar} width={420}>
                <div className="px-2 py-1">
                    <div className="flex items-center gap-2.5 mb-3 text-vitalia-600 dark:text-vitalia-400">
                        <HiOutlineQuestionMarkCircle className="text-2xl" />
                        <h5 className="!mb-0 text-base font-bold text-gray-900 dark:text-white">{title}</h5>
                    </div>
                    <p className="mb-6 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{description}</p>
                    <div className="text-right flex justify-end gap-2">
                        <Button size="sm" onClick={rechazar}>
                            No, gracias
                        </Button>
                        <Button size="sm" variant="solid" onClick={aceptar}>
                            Sí, iniciar
                        </Button>
                    </div>
                </div>
            </Dialog>

            {/* Botón flotante para reiniciar el recorrido cuando el usuario lo desee */}
            {showHelpButton && (
                <div className="fixed bottom-6 left-6 z-30">
                    <Tooltip title="Ver guía paso a paso de esta pantalla">
                        <button
                            type="button"
                            onClick={iniciarManual}
                            className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-white dark:bg-gray-800 text-vitalia-600 dark:text-vitalia-300 font-bold text-xs shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 hover:border-vitalia-400 hover:scale-105 transition-all group"
                        >
                            <HiOutlineQuestionMarkCircle className="text-lg text-vitalia-600 dark:text-vitalia-400 group-hover:rotate-12 transition-transform" />
                            <span className="hidden sm:inline">Guía de la página</span>
                        </button>
                    </Tooltip>
                </div>
            )}

            <Joyride
                key={tourInstanceKey}
                run={runTour}
                steps={steps}
                continuous
                scrollToFirstStep
                scrollOffset={scrollOffset}
                showSkipButton
                showProgress
                disableOverlayClose
                locale={JOYRIDE_LOCALE}
                styles={JOYRIDE_STYLES}
                callback={handleJoyrideCallback}
            />
        </>
    )
}
